/**
 * @file
 * Behaviors for Corner Cap Boxes.
 */
/* eslint-disable max-len */

!((document, Drupal) => {
  'use strict';

  /**
   * Setup and attach the Corner Cap Boxes behaviors.
   *
   * @type {Drupal~behavior}
   */
  Drupal.behaviors.cornerCapBoxes = {
    attach: function(context) {
      const $$cornerCapBoxes = context.querySelectorAll('.corner-cap-boxes');

      // Filter an object, similar to `Array.prototype.filter`.
      // From https://masteringjs.io/tutorials/fundamentals/filter-object .
      const filterObject = (obj, callback) => {
        return Object.fromEntries(
          Object.entries(obj).filter(([key, val]) => callback(val, key))
        );
      };

      // First, we determine what x coordinates have columns and what
      // y coordinates have rows.
      const getRowsAndColumns = ($boxSet) => {
        const $$boxes = $boxSet.querySelectorAll('.corner-cap-boxes__box');
        const length = $$boxes.length;
        const boundingClientRects = [];
        const rows = {}; // An object, not an array.
        const columns = {}; // Same.

        // Collect all the boundingClientRects in one array.
        for (let i = 0; i < length; i++) {
          let $box = $$boxes[i];
          boundingClientRects.push($box.getBoundingClientRect());
        }

        // Iterate over the rects.
        for (let i = 0; i < length; i++) {
          let rect = boundingClientRects[i];
          // If we've already logged a box in this row....
          if (rect.y in rows) {
            // Increment the count of how many are in the row. The property
            // names of rows are their y pixel coordinates. (Rows differ by y,
            // columns differ by x.)
            rows[rect.y].count++;

            // Each row keeps track of the columns within it. If the column
            // (that is, the x coordinate) of the current box isn't logged yet,
            // we add it here.
            if (rows[rect.y].columnKeys.indexOf(rect.x) === -1) {
              rows[rect.y].columnKeys.push(parseFloat(rect.x));
            }
          }
          // If we haven't already logged a box in this row....
          else {
            // Create our row object and immediately log the column.
            rows[rect.y] = {
              count: 1,
              columnKeys: [rect.x]
            };
          }

          // This is exactly the same as the above, but for columns
          // instead of rows.
          if (rect.x in columns) {
            columns[rect.x].count++;

            if (columns[rect.x].rowKeys.indexOf(rect.y) === -1) {
              columns[rect.x].rowKeys.push(parseFloat(rect.y));
            }
          }
          else {
            columns[rect.x] = {
              count: 1,
              rowKeys: [rect.y]
            };
          }
        }

        // Get row coordinates and sort them from top to bottom (low y to high y).
        const rowKeys = Object.keys(rows)
          .map((key) => parseFloat(key))
          .sort((a, b) => a - b);

        const firstRowKey = rowKeys[0];
        const lastRowKey = rowKeys[rowKeys.length - 1];

        // Column is in rows before the last row.
        const columnsInInitialRows = filterObject(
          columns,
          (column) => column.rowKeys.indexOf(firstRowKey) !== -1
        );

        // Column is in last row. This might have unique columns if the last row is centered.
        const columnsInLastRow = filterObject(
          columns,
          (column) => column.rowKeys.indexOf(lastRowKey) !== -1
        );

        // Sort the column coordinates from left to right.
        const columnsInInitialRowsKeys = Object.keys(columnsInInitialRows)
          .map((key) => parseFloat(key))
          .sort((a, b) => a - b);
        const columnsInLastRowKeys = Object.keys(columnsInLastRow)
          .map((key) => parseFloat(key))
          .sort((a, b) => a - b);

        return [
          boundingClientRects,
          rowKeys,
          columnsInInitialRowsKeys,
          columnsInLastRowKeys
        ];
      };

      // Now that we have determined our rows and columns, we're ready to
      // give the boxes the data attributes they need to display the proper
      // corner caps. There are sixteen possible combinations of borders,
      // based on whether there is or isn't another box adjacent to the current
      // box's top, right, bottom, and left sides.
      //
      // If there is a box adjacent to a side, it's marked as `in`, since that
      // side faces inside the set. If not, it's marked as `out`, since it
      // faces outside the set. So a full combination is noted as something like
      // `out-in-in-out`, representing the top, right, bottom, and left sides
      // in that order.
      const tagBoxes = ($boxSet) => {
        const [
          boundingClientRects,
          rowKeys,
          columnsInInitialRowsKeys,
          columnsInLastRowKeys
        ] = getRowsAndColumns($boxSet);

        const $$cornerCapBoxes = $boxSet.querySelectorAll(
          '.corner-cap-boxes__box'
        );

        const length = boundingClientRects.length;

        for (let i = 0; i < length; i++) {
          const rect = boundingClientRects[i];
          const $box = $$cornerCapBoxes[i];
          const boxIsInLastRow = rowKeys.indexOf(rect.y) === rowKeys.length - 1;

          // Top and bottom are straightforward; you only need to know
          // whether there is a row above/below. Even if the penultimate row
          // has 5 boxes and the bottom row has 1, every box in the penultimate
          // row must be marked `in` at the bottom for the borders to line up.
          let adjacentTop = rowKeys.indexOf(rect.y) > 0;
          let adjacentBottom = rowKeys.indexOf(rect.y) !== rowKeys.length - 1;

          // Right and left are trickier due to the last row possibly containing
          // fewer items.
          let adjacentRight;
          let adjacentLeft;

          if (boxIsInLastRow) {
            adjacentRight =
              columnsInLastRowKeys.indexOf(rect.x) <
              columnsInLastRowKeys.length - 1;
          }
          else {
            adjacentRight =
              columnsInInitialRowsKeys.indexOf(rect.x) <
              columnsInInitialRowsKeys.length - 1;
          }

          if (boxIsInLastRow) {
            adjacentLeft = columnsInLastRowKeys.indexOf(rect.x) > 0;
          }
          else {
            adjacentLeft = columnsInInitialRowsKeys.indexOf(rect.x) > 0;
          }

          $box.setAttribute('data-adjacent-top', adjacentTop);
          $box.setAttribute('data-adjacent-right', adjacentRight);
          $box.setAttribute('data-adjacent-bottom', adjacentBottom);
          $box.setAttribute('data-adjacent-left', adjacentLeft);

          // Combine all four 'in's or 'out's into a single string, to grab
          // the proper border-image-source. When converting `adjacentSide`
          // booleans to 'in' or 'out', `true === in` and `false === out`.
          //
          // The final result of this array reduce will be something like
          // 'out-in-in-out'.
          const adjacentAll = [
            adjacentTop,
            adjacentRight,
            adjacentBottom,
            adjacentLeft
          ].reduce((cumulative, current) => {
            let additional = '';

            if (cumulative) {
              additional = additional + '-';
            }

            additional += current ? 'in' : 'out';

            return cumulative + additional;
          }, '');

          $box.setAttribute('data-adjacent-all', adjacentAll);
        }
      };

      const length = $$cornerCapBoxes.length;

      for (let i = 0; i < length; i++) {
        let $boxSet = $$cornerCapBoxes[i];
        let timeout = false;
        window.addEventListener(
          'DOMContentLoaded',
          tagBoxes.bind(null, $boxSet)
        );

        window.addEventListener('resize', function() {
          window.clearTimeout(timeout);
          timeout = window.setTimeout(tagBoxes.bind(null, $boxSet), 100);
        });
      }
    }
  };
})(document, Drupal);
